import type {PtExclusions} from "./paymentTermsUtils.ts";
import {TIER_LABEL, tierOf} from "./paymentTermsUtils.ts";
import type {PtSupplier} from "~/experiments/pages/payment-terms/PaymentTermsClasses.ts";

const EXPORT_HEADER = [
    'Parent supplier',
    'Opportunity type',
    'Total spend (period)',
    'Weighted avg term (days)',
    'Target term (days)',
    'Term gap (days)',
    'Best existing term (days)',
    'Quick win opportunity',
    'Working capital opportunity',
    'Invoice count',
    'Immediate spend',
    'Below-target spend',
    'Posted-after-due spend',
    'Discount-at-risk spend',
    'Terms in use',
    'Meets policy',
    'Review status',
    'Exclusion reason',
];

const EXPORT_BASENAME = 'payment-terms-opportunities';

type ExportCell = string | number | boolean;

function buildExportRows(rows: PtSupplier[], exclusions: PtExclusions): ExportCell[][] {
    return rows.map(r => [
        r.supplier,
        TIER_LABEL[tierOf(r)],
        r.spend,
        r.currentTerm,
        r.targetTerm,
        r.gapDays,
        r.bestExistingTerm ?? '',
        Math.round(r.quickWinOpportunity),
        Math.round(r.wcOpportunity),
        r.invoiceCount,
        r.immediateSpend,
        r.belowTargetSpend,
        r.postedAfterDueSpend,
        r.discountAtRiskSpend,
        r.distinctTermCount,
        r.meetsPolicy,
        exclusions[r.id] ? 'Excluded' : 'Potential',
        exclusions[r.id] ?? '',
    ]);
}

function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function csvEscape(value: ExportCell): string {
    const s = String(value);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

export function exportPaymentTermsCsv(rows: PtSupplier[], exclusions: PtExclusions = {}) {
    const lines = buildExportRows(rows, exclusions).map(row => row.map(csvEscape).join(','));
    const csv = [EXPORT_HEADER.join(','), ...lines].join('\n');
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    downloadBlob(blob, `${EXPORT_BASENAME}.csv`);
}

export async function exportPaymentTermsExcel(rows: PtSupplier[], exclusions: PtExclusions = {}) {
    // Same exceljs build the MUI data grid premium Excel export uses; loaded lazily.
    const excel = await import('@mui/x-internal-exceljs-fork');
    const workbook = new excel.Workbook();
    const worksheet = workbook.addWorksheet('Opportunities');

    worksheet.columns = EXPORT_HEADER.map(header => ({
        header,
        width: Math.max(14, header.length + 2),
    }));
    worksheet.getRow(1).font = {bold: true};
    worksheet.views = [{state: 'frozen', ySplit: 1}];
    buildExportRows(rows, exclusions).forEach(row => worksheet.addRow(row));

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    downloadBlob(blob, `${EXPORT_BASENAME}.xlsx`);
}
